package dev.kuml.portal.web.i18n.legal

/**
 * R-18 §6.1 -- structural model for the three legal pages' content, rendered by
 * `dev.kuml.portal.web.legal.legalScreen`. Replaces three hand-written, per-language render
 * functions with ONE render function plus data: the render logic cannot drift between languages
 * (there is only one implementation), and the two languages are free to differ structurally where
 * a translation legitimately needs to (e.g. an extra courtesy-translation notice) without forking
 * any Kotlin code.
 */
public sealed interface LegalBlock {
    public data class Heading(
        val text: String,
    ) : LegalBlock

    public data class Paragraph(
        val text: String,
    ) : LegalBlock

    /** A paragraph mixing plain text and inline links -- e.g. "Zur Ausübung genügt eine formlose
     * E-Mail an [irakli@betchvaia.de]." */
    public data class Rich(
        val segments: List<Segment>,
    ) : LegalBlock {
        public sealed interface Segment {
            public data class Text(
                val text: String,
            ) : Segment

            public data class Link(
                val text: String,
                val href: String,
                val newTab: Boolean = false,
            ) : Segment
        }
    }

    public data class Bullets(
        val items: List<String>,
    ) : LegalBlock

    /** A short multi-line block, e.g. a postal address's street/city lines rendered one per row --
     * distinct from [Bullets] (no list markers) and from separate [Paragraph]s (no paragraph
     * spacing between the lines). */
    public data class Lines(
        val lines: List<String>,
    ) : LegalBlock
}

/** @property title the page's `<h1>`.
 * @property notice an optional courtesy-translation disclaimer shown directly under the title (see
 *   this wave's plan §6.3) -- `null` on every German page (the German text is always the legally
 *   authoritative one, so it needs no disclaimer about itself). */
public data class LegalPage(
    val title: String,
    val notice: String?,
    val blocks: List<LegalBlock>,
)

public interface LegalPages {
    public val imprint: LegalPage
    public val privacy: LegalPage
    public val terms: LegalPage
}

// ---------- Small builder DSL shared by LegalPagesDe/LegalPagesEn ----------
// Kept here (not duplicated per file) so both languages build their [LegalPage]s through the
// exact same helpers -- a structural difference between the two can only ever come from the DATA
// each file passes in, never from a divergent builder implementation.

internal fun legalPage(
    title: String,
    notice: String? = null,
    build: MutableList<LegalBlock>.() -> Unit,
): LegalPage {
    val blocks = mutableListOf<LegalBlock>()
    blocks.build()
    return LegalPage(title, notice, blocks)
}

internal fun MutableList<LegalBlock>.h(text: String) {
    add(LegalBlock.Heading(text))
}

internal fun MutableList<LegalBlock>.p(text: String) {
    add(LegalBlock.Paragraph(text))
}

internal fun MutableList<LegalBlock>.rich(vararg segments: LegalBlock.Rich.Segment) {
    add(LegalBlock.Rich(segments.toList()))
}

internal fun MutableList<LegalBlock>.bullets(vararg items: String) {
    add(LegalBlock.Bullets(items.toList()))
}

internal fun MutableList<LegalBlock>.lines(vararg items: String) {
    add(LegalBlock.Lines(items.toList()))
}

internal fun txt(text: String): LegalBlock.Rich.Segment = LegalBlock.Rich.Segment.Text(text)

internal fun lnk(
    text: String,
    href: String,
    newTab: Boolean = false,
): LegalBlock.Rich.Segment = LegalBlock.Rich.Segment.Link(text, href, newTab)
